import { BenchAnchorPoint, Equipment } from 'fitbeat.models';
import { UserExerciseSession } from '../../entities';
import { BandWeightAdjustment } from './BandWeightAdjustment';
import { homeEquipmentWeightInfoMapping } from './equimpentWeightMaps';
import {
  EquipmentWeightForExerciseInZone,
  IEquipmentWeightForExerciseInZone,
  IWeightAdjustmentStrategy,
} from './EquipmentWeightCalculator';

type EquipmentWeightsGroupedByAnchorPoint = {
  [key in BenchAnchorPoint]?: IEquipmentWeightForExerciseInZone[];
};

export class QuadFormatWeightAdjustment
  extends BandWeightAdjustment
  implements IWeightAdjustmentStrategy {
  public adjustExerciseWeights(
    exerciseSession: UserExerciseSession,
    equipmentMap: EquipmentWeightForExerciseInZone,
  ) {
    for (const key of Object.keys(equipmentMap)) {
      const equipmentWeightList: IEquipmentWeightForExerciseInZone[] =
        equipmentMap[key];
      if (key === Equipment.UniversalBands) {
        this.adjustEquipmentWeightsForUniversalBands(
          equipmentWeightList,
          exerciseSession,
        );
        return;
      }
      const lightestWeightInfo =
        this.getLightestWeight(equipmentWeightList).equipmentWeightInfo;
      this.setEquipmentWeightInfoForExercisesInZone(
        exerciseSession,
        equipmentWeightList,
        lightestWeightInfo,
      );
    }
  }
  private adjustEquipmentWeightsForUniversalBands(
    equipmentWeightList: IEquipmentWeightForExerciseInZone[],
    exerciseSession: UserExerciseSession,
  ) {
    const groupedByAnchorPoint =
      this.groupEquipmentByAnchorPoint(equipmentWeightList);
    const anchorPoints = Object.keys(groupedByAnchorPoint).filter(
      (item) => item !== BenchAnchorPoint.NONE,
    );
    if (anchorPoints.length > 1) {
      throw new Error(`Found more than one anchor point in zone`);
    }
    const anchored = groupedByAnchorPoint[anchorPoints[0] as BenchAnchorPoint];
    const nonAnchored = groupedByAnchorPoint[BenchAnchorPoint.NONE];
    const onlyHasAnchored = anchored && !nonAnchored;
    const onlyHasNonAnchored = !anchored && nonAnchored;
    if (onlyHasAnchored) {
      this.setEquipmentWeight(anchored, exerciseSession);
      return;
    }
    if (onlyHasNonAnchored) {
      return;
    }
    if (anchored && nonAnchored) {
      const lightestWeightForAnchored = this.getLightestWeight(anchored);
      this.adjustNonAnchoredWeights(
        nonAnchored,
        lightestWeightForAnchored,
        exerciseSession,
      );
      this.setEquipmentWeight(anchored, exerciseSession);
    }
  }
  private groupEquipmentByAnchorPoint(
    equipmentWeightList: IEquipmentWeightForExerciseInZone[],
  ): EquipmentWeightsGroupedByAnchorPoint {
    const groupedByAnchorPoint: EquipmentWeightsGroupedByAnchorPoint = {};
    equipmentWeightList.reduce((acc, cur) => {
      const anchorPoint = cur.anchorPoint || BenchAnchorPoint.NONE;
      if (!acc[anchorPoint]) {
        acc[anchorPoint] = [cur];
        return acc;
      }
      acc[anchorPoint]?.push(cur);
      return acc;
    }, groupedByAnchorPoint);
    return groupedByAnchorPoint;
  }

  private setEquipmentWeight(
    equipmentWeightList: IEquipmentWeightForExerciseInZone[],
    exerciseSession: UserExerciseSession,
    lowerWeight?: IEquipmentWeightForExerciseInZone,
  ) {
    const lightestEquipmentWeightInfo =
      lowerWeight?.equipmentWeightInfo ??
      this.getLightestWeight(equipmentWeightList).equipmentWeightInfo;
    this.setEquipmentWeightInfoForExercisesInZone(
      exerciseSession,
      equipmentWeightList,
      lightestEquipmentWeightInfo,
    );
  }

  private adjustNonAnchoredWeights(
    nonAnchoredWeights: IEquipmentWeightForExerciseInZone[],
    lightestAnchoredWeight: IEquipmentWeightForExerciseInZone,
    exerciseSession: UserExerciseSession,
  ) {
    const adjustedNonAnchoredWeights: IEquipmentWeightForExerciseInZone[] =
      nonAnchoredWeights.map((weight: IEquipmentWeightForExerciseInZone) => {
        const shouldLowerWeight =
          lightestAnchoredWeight.adjustedExerciseResistance ===
            weight.adjustedExerciseResistance &&
          weight.adjustedExerciseResistance > 1;
        if (shouldLowerWeight) {
          const weightAndColor = homeEquipmentWeightInfoMapping
            .get(Equipment.UniversalBands)
            ?.get(weight.adjustedExerciseResistance - 1);
          const lowerWeight: IEquipmentWeightForExerciseInZone = {
            ...weight,
            adjustedExerciseResistance: weight.adjustedExerciseResistance - 1,
            equipmentWeightInfo: {
              ...weight.equipmentWeightInfo,
              equipmentWeight: weightAndColor!.weight,
              color: weightAndColor!.color,
            },
          };
          return lowerWeight;
        }
        return weight;
      });
    const exerciseInOrder = exerciseSession.exerciseInOrder();
    const adjustedExercises = exerciseInOrder.map((exercise,i)=> {
      const exerciseClone = structuredClone(exercise);
      const adjustedWeightInfo = adjustedNonAnchoredWeights.find((weightInfo)=> weightInfo.indexOfExercise === i);
      if (adjustedWeightInfo) {
        exerciseClone.equipmentWeightInfo = adjustedWeightInfo.equipmentWeightInfo;
      }
      return exerciseClone;
    });
    exerciseSession.setExercisesInOrder(adjustedExercises);
  }
}
